import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { TextInput } from '../../../../form/textInput';
import { operationType, selectDataType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { Textarea } from '../../../../form/textarea';
import { Select } from '../../../../form/select';
import { Main, Styled } from './style';
import { AuthenticateSelect } from '../../../../auth/select';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';

type WooCommerceProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

type ResourceType = 'customer' | 'order' | 'product';
type OperationType = 'create' | 'createMany' | 'get' | 'getMany' | 'update' | 'delete';

type RequestState = { resource: ResourceType; operation: OperationType };

const resourceOptions: selectDataType[] = [
   { id: 1, name: 'Customer', refId: 'customer', isActive: false },
   { id: 2, name: 'Order', refId: 'order', isActive: false },
   { id: 3, name: 'Product', refId: 'product', isActive: true }
];

const operationOptionsTemplate: selectDataType[] = [
   { id: 1, name: 'Create', refId: 'create', isActive: true },
   { id: 2, name: 'Create Many', refId: 'createMany', isActive: false },
   { id: 3, name: 'Get', refId: 'get', isActive: false },
   { id: 4, name: 'Get Many', refId: 'getMany', isActive: false },
   { id: 5, name: 'Update', refId: 'update', isActive: false },
   { id: 6, name: 'Delete', refId: 'delete', isActive: false }
];

const requestStateMap: Record<string, RequestState> = {
   createCustomer: { resource: 'customer', operation: 'create' },
   createCustomers: { resource: 'customer', operation: 'createMany' },
   getCustomer: { resource: 'customer', operation: 'get' },
   getCustomers: { resource: 'customer', operation: 'getMany' },
   updateCustomer: { resource: 'customer', operation: 'update' },
   deleteCustomer: { resource: 'customer', operation: 'delete' },
   createOrder: { resource: 'order', operation: 'create' },
    createOrders: { resource: 'order', operation: 'createMany' },
   getOrder: { resource: 'order', operation: 'get' },
   getOrders: { resource: 'order', operation: 'getMany' },
   updateOrder: { resource: 'order', operation: 'update' },
   deleteOrder: { resource: 'order', operation: 'delete' },
   createProduct: { resource: 'product', operation: 'create' },
   createProducts: { resource: 'product', operation: 'createMany' },
   getProduct: { resource: 'product', operation: 'get' },
   getProducts: { resource: 'product', operation: 'getMany' },
   updateProduct: { resource: 'product', operation: 'update' },
   deleteProduct: { resource: 'product', operation: 'delete' }
};

const requestDocs: { [key: string]: string } = {
   createCustomer: `Required:
- Payload JSON object
- email field in payload
Payload Example:
{
  "email": "alice@example.com",
  "first_name": "Alice",
  "last_name": "Johnson"
}
Notes:
- billing, shipping, password are optional
- payload must be valid JSON object text`,
   createCustomers: `Required:
- Payload JSON array of customer objects
Payload Example:
[
  { "email": "alice@example.com", "first_name": "Alice" },
  { "email": "bob@example.com", "first_name": "Bob" }
]
Also Supported:
{
  "create": [
    { "email": "alice@example.com" },
    { "email": "bob@example.com" }
  ]
}
Notes:
- sends POST /customers/batch
- each item should be a customer object`,
   getCustomer: `Required:
- Customer ID
Optional Query:
{
  "context": "view"
}
Notes:
- use customerId field
- query must be valid JSON object text`,
   getCustomers: `Optional Query:
{
  "per_page": 50,
  "search": "alice"
}
Notes:
- default per_page is 50
- per_page range is 1..100
- limit is supported and mapped to per_page`,
   updateCustomer: `Required:
- Customer ID
- Payload JSON object
Payload Example:
{
  "first_name": "Alice",
  "last_name": "Johnson",
  "billing": {
    "phone": "+1-555-0100"
  }
}
Notes:
- only send fields you need to update`,
   deleteCustomer: `Required:
- Customer ID
Optional Query:
{
  "force": true
}
Notes:
- force accepts true/false, 1/0, yes/no, on/off, y/n`,
   createOrder: `Required:
- Payload JSON object
- line_items in payload
Payload Example:
{
  "payment_method": "cod",
  "set_paid": false,
  "billing": {
    "first_name": "Alice",
    "last_name": "Johnson",
    "email": "alice@example.com"
  },
  "line_items": [
    { "product_id": 88, "quantity": 1 }
  ]
}
Notes:
- customer_id can be used instead of full customer fields`,
   createOrders: `Required:
- Payload JSON array of order objects
Payload Example:
[
  {
    "payment_method": "cod",
    "set_paid": false,
    "line_items": [{ "product_id": 88, "quantity": 1 }]
  },
  {
    "payment_method": "cod",
    "set_paid": false,
    "line_items": [{ "product_id": 89, "quantity": 2 }]
  }
]
Also Supported:
{
  "create": [ ... ]
}
Notes:
- sends POST /orders/batch
- each item should be an order object`,
   getOrder: `Required:
- Order ID
Optional Query:
{
  "context": "view"
}
Notes:
- use orderId field`,
   getOrders: `Optional Query:
{
  "per_page": 25,
  "status": "processing"
}
Notes:
- default per_page is 50
- per_page range is 1..100
- limit is supported and mapped to per_page`,
   updateOrder: `Required:
- Order ID
- Payload JSON object
Payload Example:
{
  "status": "completed",
  "customer_note": "Packed and shipped"
}
Notes:
- only send fields you need to update`,
   deleteOrder: `Required:
- Order ID
Optional Query:
{
  "force": true
}
Notes:
- force accepts true/false, 1/0, yes/no, on/off, y/n`,
   createProduct: `Required:
- Payload JSON object
- name, type, regular_price (recommended minimum)
Payload Example:
{
  "name": "Premium Hoodie",
  "type": "simple",
  "regular_price": "59.99",
  "description": "Premium cotton hoodie"
}
Notes:
- categories, tags, images, stock fields are optional`,
   createProducts: `Required:
- Payload JSON array of product objects
Payload Example:
[
  { "name": "Premium Hoodie", "type": "simple", "regular_price": "59.99" },
  { "name": "Basic Cap", "type": "simple", "regular_price": "19.99" }
]
Also Supported:
{
  "create": [ ... ]
}
Notes:
- sends POST /products/batch
- each item should be a product object`,
   getProduct: `Required:
- Product ID
Optional Query:
{
  "context": "view"
}
Notes:
- use productId field`,
   getProducts: `Optional Query:
{
  "per_page": 50,
  "search": "hoodie"
}
Notes:
- default per_page is 50
- per_page range is 1..100
- limit is supported and mapped to per_page`,
   updateProduct: `Required:
- Product ID
- Payload JSON object
Payload Example:
{
  "name": "Premium Hoodie - Updated",
  "regular_price": "64.99"
}
Notes:
- only send fields you need to update`,
   deleteProduct: `Required:
- Product ID
Optional Query:
{
  "force": true
}
Notes:
- force accepts true/false, 1/0, yes/no, on/off, y/n`
};

const getRequestFromState = (resource: ResourceType, operation: OperationType) => {
   if (operation === 'createMany') return `create${resource.charAt(0).toUpperCase() + resource.slice(1)}s`;
   if (operation === 'getMany') return `get${resource.charAt(0).toUpperCase() + resource.slice(1)}s`;
   return `${operation}${resource.charAt(0).toUpperCase() + resource.slice(1)}`;
};

const getIdMeta = (resource: ResourceType) => {
   if (resource === 'customer') return { keyRef: 'customerId', title: 'Customer ID', placeholder: '123' };
   if (resource === 'order') return { keyRef: 'orderId', title: 'Order ID', placeholder: '123' };
   return { keyRef: 'productId', title: 'Product ID', placeholder: '123' };
};

export function WooCommerce({ id, data, setChildMinimize, setChildSettings }: WooCommerceProps) {
   const { setNodes } = useReactFlow();
   const [resources, setResources] = React.useState<selectDataType[]>(resourceOptions);
   const [operations, setOperations] = React.useState<selectDataType[]>(operationOptionsTemplate);
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);

   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, {
            resource: 'product',
            operation: 'create',
            request: 'createProduct',
            payload: '{\n  "name": "Sample Product"\n}',
            query: '{\n  "per_page": 50\n}',
            force: false
         });
         return;
      }

      const request = data.value.request as string;
      const state = requestStateMap[request] || { resource: 'product', operation: 'create' as OperationType };
      setResources(prev => prev.map(item => ({ ...item, isActive: item.refId === state.resource })));
      setOperations(prev => prev.map(item => ({ ...item, isActive: item.refId === state.operation })));
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: connectionData });
      } else {
         updateNodeValue(id, setNodes, { connection: null });
      }
   };

   const applyRequestState = (resource: ResourceType, operation: OperationType) => {
      const request = getRequestFromState(resource, operation);
      updateNodeValue(id, setNodes, { resource, operation, request });
   };

   const handleSelectResource = (selectId: number) => {
      const selectedResource = resources.find(item => item.id === selectId)?.refId as ResourceType;
      if (!selectedResource) return;

      setResources(prev => prev.map(item => ({ ...item, isActive: item.id === selectId })));
      const activeOperation = (operations.find(item => item.isActive)?.refId as OperationType) || 'create';
      applyRequestState(selectedResource, activeOperation);
   };

   const handleSelectOperation = (selectId: number) => {
      const selectedOperation = operations.find(item => item.id === selectId)?.refId as OperationType;
      if (!selectedOperation) return;

      setOperations(prev => prev.map(item => ({ ...item, isActive: item.id === selectId })));
      const activeResource = (resources.find(item => item.isActive)?.refId as ResourceType) || 'product';
      applyRequestState(activeResource, selectedOperation);
   };

   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   };

   const activeResource = ((resources.find(item => item.isActive)?.refId as ResourceType) || data.value?.resource || 'product') as ResourceType;
   const activeOperation = ((operations.find(item => item.isActive)?.refId as OperationType) || data.value?.operation || 'create') as OperationType;
   const activeRequest = data.value?.request || getRequestFromState(activeResource, activeOperation);

   const isIdOperation = ['get', 'update', 'delete'].includes(activeOperation);
   const isPayloadOperation = ['create', 'createMany', 'update'].includes(activeOperation);
   const isQueryOperation = ['get', 'getMany', 'delete'].includes(activeOperation);
   const isDeleteOperation = activeOperation === 'delete';
   const isWarning = !data.value?.connection;
   const idMeta = getIdMeta(activeResource);

   return (
      <>
         <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
         <Main>
            {isWarning ? (
               <>
                  <div id="warning"></div>
                  <span id="warningIcon"><IoWarningOutline size={ 21 } /></span>
               </>
            ) : data.output ? (
               <>
                  <div id="ran"></div>
                  <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
               </>
            ) : null}

            {isExpanded ? (
               <ExpandedBlock
                  data={{ id, ...data }}
                  handleClose={ () => setIsExpanded(false) }
                  isWarning={ isWarning }
                  style={{ minWidth: '420px' }}
               >
                  <Styled>
                     <div className="action">
                        <AuthenticateSelect
                           type="woocommerce"
                           defaultId={ data.value?.connection?.id }
                           handleAuthenticate={ handleAuthenticate }
                           background={ data.background }
                        />
                     </div>

                     <Select title="Resource" data={ resources } handleSelectItem={ handleSelectResource } isSmall={ true } />
                     <Select title="Operation" data={ operations } handleSelectItem={ handleSelectOperation } isSmall={ true } />

                     {isIdOperation ? (
                        <TextInput
                           keyRef={ idMeta.keyRef }
                           value={ data.value?.[idMeta.keyRef] }
                           title={ idMeta.title }
                           placeholder={ idMeta.placeholder }
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isPayloadOperation ? (
                        <Textarea
                           title="Payload"
                           keyRef="payload"
                           value={ data.value?.payload }
                           placeholder={
                              activeOperation === 'createMany'
                                 ? '[\n  { "name": "Sample 1" },\n  { "name": "Sample 2" }\n]'
                                 : '{\n  "name": "Sample"\n}'
                           }
                           handleChange={ (e) => updateNodeValue(id, setNodes, { payload: e.target.value }) }
                        />
                     ) : null}

                     {isQueryOperation ? (
                        <Textarea
                           title="Query"
                           keyRef="query"
                           value={ data.value?.query }
                           placeholder={'{\n  "per_page": 50\n}'}
                           handleChange={ (e) => updateNodeValue(id, setNodes, { query: e.target.value }) }
                        />
                     ) : null}

                     {isDeleteOperation ? (
                        <TextInput
                           keyRef="force"
                           value={ String(data.value?.force ?? false) }
                           title="Force"
                           placeholder="true or false"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     <div className="previewSection">
                        <h5>Function Block Preview</h5>
                        <pre>{ requestDocs[activeRequest] || 'No function metadata available.' }</pre>
                     </div>
                  </Styled>
               </ExpandedBlock>
            ) : null}
         </Main>
      </>
   );
}